import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createAppTheme } from "./index";

const STORAGE_KEY = "portfolio:color-mode";

const ColorModeContext = createContext({ mode: "light", toggleMode: () => {} });

/** `{ mode, toggleMode }` for whichever component owns the theme switch. */
export const useColorMode = () => useContext(ColorModeContext);

const prefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-color-scheme: dark)").matches;

const readStoredMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Private mode or blocked storage: fall back to the system preference.
    return null;
  }
};

export const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(
    () => readStoredMode() ?? (prefersDark() ? "dark" : "light")
  );

  const toggleMode = useCallback(() => {
    setMode((current) => {
      const next = current === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* preference just won't persist */
      }
      return next;
    });
  }, []);

  // Follow the OS until the visitor makes an explicit choice.
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return undefined;

    const handleChange = (event) => {
      if (!readStoredMode()) setMode(event.matches ? "dark" : "light");
    };

    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, []);

  // Keep the browser chrome (mobile address bar) in sync with the theme.
  useEffect(() => {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", mode === "dark" ? "#0a0a0a" : "#ffffff");
  }, [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ColorModeProvider;
